import {userStore} from '@js/stores/userStore';
import {mapStore} from '@js/stores/mapStore';

import {showProductModal} from '@js/actions/basketActions';
import {pushError} from '@js/actions/errorManagement';

import {resolveMaxDistanceMeters} from '@js/components/modules/constants';

import {MAP_CLASS, MAP_MODULES} from '@js/modules/maps/mapConstants';


const CLASS_IMPORTERS = {
    [MAP_CLASS.RIDE]: () => import(/* webpackChunkName: "maps-ride" */ '@js/modules/maps/rideMap'),
    [MAP_CLASS.SEARCH]: () => import(/* webpackChunkName: "maps-search" */ '@js/modules/maps/searchMap'),
    [MAP_CLASS.BUILD]: () => import(/* webpackChunkName: "maps-build" */ '@js/modules/maps/buildMap'),
    [MAP_CLASS.EXPLORE]: () => import(/* webpackChunkName: "maps-explore" */ '@js/modules/maps/exploreMap'),
    [MAP_CLASS.PLAN]: () => import(/* webpackChunkName: "maps-plan" */ '@js/modules/maps/planMap')
};

const MODULE_IMPORTERS = {
    [MAP_MODULES.TOOLBAR]: () => import(/* webpackChunkName: "maps-toolbar" */ '@js/modules/maps/modules/toolbarMap'),
    [MAP_MODULES.RIDE_MARKERS]: () => import(/* webpackChunkName: "maps-ride-markers" */ '@js/modules/maps/modules/rideMarkersMap'),
    [MAP_MODULES.RIDE_TRACK]: () => import(/* webpackChunkName: "maps-ride-track" */ '@js/modules/maps/modules/rideTrackMap'),
    [MAP_MODULES.RIDE_POIS]: () => import(/* webpackChunkName: "maps-ride-pois" */ '@js/modules/maps/modules/ridePoisMap'),
    [MAP_MODULES.RIDE_STORE]: () => import(/* webpackChunkName: "maps-ride-store" */ '@js/modules/maps/modules/rideStoreMap'),
    [MAP_MODULES.RIDE_SHARE]: () => import(/* webpackChunkName: "maps-ride-share" */ '@js/modules/maps/modules/rideShareMap'),
    [MAP_MODULES.ELEVATION_COMPUTATION]: () => import(/* webpackChunkName: "maps-elevation-computation" */ '@js/modules/maps/modules/elevationComputationMap'),
    [MAP_MODULES.COMPUTE_ROAD]: () => import(/* webpackChunkName: "maps-compute-road" */ '@js/modules/maps/modules/computeRoadMap'),
    [MAP_MODULES.DRAW_LINE]: () => import(/* webpackChunkName: "maps-draw-line" */ '@js/modules/maps/modules/drawLineMap'),
    [MAP_MODULES.FREEHAND]: () => import(/* webpackChunkName: "maps-freehand-draw" */ '@js/modules/maps/modules/freehandDrawMap'),
    [MAP_MODULES.GRAPH]: () => import(/* webpackChunkName: "maps-graph" */ '@js/modules/maps/modules/graphMap'),
    [MAP_MODULES.MARKER_VISIBILITY]: () => import(/* webpackChunkName: "maps-marker-visibility" */ '@js/modules/maps/modules/markerVisibilityMap'),
    [MAP_MODULES.GEOLOCATION]: () => import(/* webpackChunkName: "maps-geolocation" */ '@js/modules/maps/modules/geolocationMap'),
    [MAP_MODULES.ISOCHRONE]: () => import(/* webpackChunkName: "maps-isochrone" */ '@js/modules/maps/modules/isochroneMap'),
    [MAP_MODULES.COLLABORATIVE]: () => import(/* webpackChunkName: "maps-collaborative" */ '@js/modules/maps/modules/collaborativeMap')
};


class MapBuilder {
    _mapClass = null;

    // Resolves once the map class chunk is loaded and _mapClass is set. Deferred modules await it
    // before mixing onto the prototype (a module can be requested before the class has landed).
    _classReady = Promise.resolve();

    // name -> promise of "module chunk downloaded AND mixed onto the prototype". Dedupes both eager
    // and on-demand loads so a module is only ever assigned once per map class.
    _modulePromises = {};

    // Dynamic chunk imports fail transiently (CDN blip, chunk hash rollover right after a deploy,
    // in-app browsers, flaky networks). Retry a few times before giving up so a one-off failure
    // doesn't leave the map missing a module's methods ("X is not a function").
    _moduleMaxAttempts = 3;

    /* Public methods
    ******************** */
    // `modules` is the FULL feature set the map is aware of (drives `hasModule` guards in the map
    // classes). `options.deferredModules` lists the advanced modules NOT loaded at boot — they are
    // fetched later via ensureModule() (e.g. when the user opens the edit menu or arms the feature).
    createMap = (mapClass, modules, params, {deferredModules = []} = {}) => {
        this._mapClass = null;
        this._modulePromises = {};

        modules.forEach((module) => {
            if (!Object.values(MAP_MODULES)
                .includes(module)) {
                throw `Unknown module to load: ${module}`;
            }
        });

        const classImporter = CLASS_IMPORTERS[mapClass];
        if (!classImporter) {
            throw new Error('Try to initialize unknown map class: ' + mapClass);
        }

        // Kick off the class chunk download and expose its readiness so base modules (whose downloads
        // start in parallel below) can mix in as soon as the prototype exists.
        this._classReady = this._importWithRetry(classImporter, mapClass)
            .then((classModule) => {
                this._mapClass = classModule.default;
            });

        const eagerModules = modules.filter((module) => !deferredModules.includes(module));

        return Promise.allSettled([this._classReady, ...eagerModules.map((name) => this._loadModule(name))])
            .then((results) => {
                results.forEach((result) => {
                    if (result.status === 'rejected') {
                        pushError(result.reason);
                    }
                });

                if (!this._mapClass) {
                    return false;
                }

                return new this._mapClass(
                    modules,
                    {
                        ...params,
                        snapToRoads: userStore.featureFlags.map_snap_to_road_enabled,
                        hasMapLimitations: userStore.featureFlags.map_limitations_enabled,
                        mapMaxRasterTilesCount: userStore.featureFlags.map_max_raster_tiles_count,
                        mapMaxVectorTilesCount: userStore.featureFlags.map_max_vector_tiles_count,
                        mapMaxRouteComputationCount: userStore.featureFlags.map_max_route_computation_count,
                        mapMaxIsochrone: userStore.featureFlags.map_max_isochrone,
                        mapMaxRoundTrip: userStore.featureFlags.map_max_round_trip,
                        mapMaxDistance: resolveMaxDistanceMeters(userStore.featureFlags.map_max_distance, mapStore.currentRideType, userStore.unit),
                        // OPTIMIZE: use it for POI mode
                        poiPlanEnabled: userStore.featureFlags.ride_plan_enabled,
                        showProductModal: showProductModal
                    }
                );
            })
            .catch((error) => {
                pushError(error);
            });
    };

    // Lazily load a deferred feature module and mix it onto the current map prototype. Safe to call
    // repeatedly (cached) and before/after the class chunk has landed. Call it just ahead of the
    // action that needs the module (feature armed, menu opened) so the chunk is ready in time.
    ensureModule = (name) => this._loadModule(name);

    ensureModules = (names = []) => Promise.all(names.map((name) => this._loadModule(name)));

    /* Private methods
    ******************** */
    // Run a chunk importer with bounded retries + linear backoff. Rejects only once every attempt
    // has failed, so the caller (and Sentry via pushError) still learns about a genuine failure.
    _importWithRetry = (importer, label, attempt = 1) => (
        importer()
            .catch((error) => {
                if (attempt >= this._moduleMaxAttempts) {
                    throw error;
                }

                return new Promise((resolve) => {
                    setTimeout(resolve, 300 * attempt);
                })
                    .then(() => this._importWithRetry(importer, label, attempt + 1));
            })
    );

    _loadModule = (name) => {
        if (this._modulePromises[name]) {
            return this._modulePromises[name];
        }

        const importer = MODULE_IMPORTERS[name];
        if (!importer) {
            return Promise.reject(new Error(`Unknown module to load: ${name}`));
        }

        // Start the download immediately (parallel with the class / other modules) but only mix the
        // module onto the prototype once the class chunk has set _mapClass.
        const promise = Promise.all([this._importWithRetry(importer, name), this._classReady])
            .then(([module]) => {
                if (this._mapClass && module && module.default) {
                    Object.assign(this._mapClass.prototype, module.default);
                }

                return module;
            })
            .catch((error) => {
                // Never leave a rejected promise pinned in the cache: drop it so a later
                // ensureModule() (feature armed, menu reopened, network recovered) retries a fresh
                // import instead of replaying the same failure.
                delete this._modulePromises[name];

                throw error;
            });

        this._modulePromises[name] = promise;

        return promise;
    };
}

export default new MapBuilder();
